import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/nvsnContent";

const inputCls =
  "w-full bg-[#0e0e0e] border border-[#2a2a2a] px-4 py-3 text-[13px] text-[#f0f0f0] placeholder:text-[#777] rounded focus:outline-none focus:border-[#4deb7a] transition-colors";
const labelCls = "block font-mono-dm uppercase tracking-[0.12em] text-[10px] text-[#bdbdbd] mb-2";

export default function Landing() {
  const navigate = useNavigate();
  const { loginInvestor, investor } = useAuth();
  const [mode, setMode] = useState("request");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", company: "", email: "" });

  if (investor) navigate("/dataroom", { replace: true });

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "request") {
        const { data } = await api.post("/auth/signup", form);
        loginInvestor(data.token, data.user);
        toast.success(`Access granted — welcome, ${data.user.first_name}.`);
      } else {
        const { data } = await api.post("/auth/login", { email: form.email });
        loginInvestor(data.token, data.user);
        toast.success(`Welcome back, ${data.user.first_name}.`);
      }
      navigate("/dataroom");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#080808] text-[#e8e8e8]">
      {/* Left — brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col justify-between p-14 border-r border-[#222]">
        <div className="absolute inset-0 opacity-[0.15]" style={{ background: "radial-gradient(circle at 30% 20%, rgba(77,235,122,0.25), transparent 55%)" }} />
        <div className="relative z-10 flex items-center gap-3">
          <img src={LOGO_URL} alt="NVSN" className="h-[42px] w-auto" />
          <span className="w-px h-4 bg-[#2a2a2a]" />
          <span className="mono-eyebrow text-[#bdbdbd]">Investor Data Room</span>
        </div>
        <div className="relative z-10">
          <div className="mono-eyebrow text-[#4deb7a] mb-5">Pre-Seed · 2026</div>
          <h1 className="font-serif-pf text-[44px] xl:text-[54px] leading-[1.1] text-[#f0f0f0]">
            The world's first<br />closed-loop health &amp; performance ecosystem.
          </h1>
          <p className="mt-6 text-[#d4d4d4] text-[14px] max-w-md leading-[1.8]">
            A vertically integrated, six-pillar health &amp; performance ecosystem powered by a lifetime longitudinal sovereign data record. This room contains the complete diligence set.
          </p>
          <div className="mt-10 flex gap-8">
            {[["$3.5M", "Raise"], ["SAFE", "Instrument"], ["$25M", "Cap"]].map(([v, l]) => (
              <div key={l}>
                <div className="font-mono-dm text-[16px] text-[#f0f0f0]">{v}</div>
                <div className="mono-eyebrow text-[#bdbdbd] mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 mono-eyebrow text-[#bdbdbd]">© NVSN Holdings · Confidential</div>
      </div>

      {/* Right — access form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 sm:px-12 py-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src={LOGO_URL} alt="NVSN" className="h-9 w-auto" />
            <span className="mono-eyebrow text-[#bdbdbd]">Data Room</span>
          </div>

          {mode === "login" && (
            <button data-testid="back-to-signup" onClick={() => setMode("request")} className="flex items-center gap-1.5 text-[12px] text-[#bdbdbd] hover:text-[#f0f0f0] mb-6 transition-colors">
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to sign up
            </button>
          )}

          <div className="mono-eyebrow text-[#bdbdbd] mb-3">{mode === "request" ? "Investor Access" : "Returning Investor"}</div>
          <h2 className="font-serif-pf text-[36px] sm:text-[42px] leading-[1.1] text-[#f0f0f0] mb-3">
            {mode === "request" ? "NVSN Data Room" : "Welcome back."}
          </h2>
          <p className="text-[#d4d4d4] text-[13px] mb-9 leading-[1.7]">
            {mode === "request"
              ? "Provide your details to enter the data room. Access is granted immediately and logged for the founding team."
              : "Enter the email you registered with to continue to the data room."}
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="access-form">
            {mode === "request" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>First name</label>
                    <input data-testid="input-first-name" required value={form.first_name} onChange={upd("first_name")} className={inputCls} placeholder="Jane" />
                  </div>
                  <div>
                    <label className={labelCls}>Last name</label>
                    <input data-testid="input-last-name" required value={form.last_name} onChange={upd("last_name")} className={inputCls} placeholder="Doe" />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Company represented</label>
                  <input data-testid="input-company" required value={form.company} onChange={upd("company")} className={inputCls} placeholder="Acme Ventures" />
                </div>
              </>
            )}
            <div>
              <label className={labelCls}>Email address</label>
              <input data-testid="input-email" type="email" required value={form.email} onChange={upd("email")} className={inputCls} placeholder="jane@acme.vc" />
            </div>

            <button
              data-testid="submit-access"
              type="submit"
              disabled={loading}
              className="group w-full bg-[#4deb7a] hover:bg-[#3dd869] text-[#080808] font-medium py-3.5 rounded flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            >
              {loading ? "Please wait…" : mode === "request" ? "Enter data room" : "Continue"}
              {!loading && <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />}
            </button>
          </form>

          <div className="mt-6 text-[13px] text-[#d4d4d4]">
            {mode === "request" ? (
              <>Already registered?{" "}
                <button data-testid="switch-login" onClick={() => setMode("login")} className="text-[#4deb7a] hover:underline underline-offset-4">Sign in</button>
              </>
            ) : (
              <>Need access?{" "}
                <button data-testid="switch-request" onClick={() => setMode("request")} className="text-[#4deb7a] hover:underline underline-offset-4">Request access</button>
              </>
            )}
          </div>

          <div className="mt-12 pt-6 flex items-center justify-between text-[11px] text-[#bdbdbd]">
            <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} /> Confidential · access logged</span>
            <button data-testid="admin-link" onClick={() => navigate("/admin")} className="hover:text-[#f0f0f0] transition-colors font-mono-dm uppercase tracking-wider">Admin</button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
